// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import FinanceAccount from "./finance-account.js"

const modelClassData = {
  "attributes": {
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": true
    },
    "name": {
      "column": null,
      "name": "name",
      "selected_by_default": null,
      "translated": true
    }
  },
  "className": "FinanceAccountType",
  "collectionKey": "finance_account_types",
  "collectionName": "finance_account_types",
  "i18nKey": "finance_account_type",
  "camelizedLower": "financeAccountType",
  "name": "FinanceAccountType",
  "nameDasherized": "finance-account-type",
  "pluralName": "finance_account_types",
  "ransackable_associations": [
    {
      "className": "FinanceAccountType::Translation",
      "collectionName": null,
      "foreignKey": "finance_account_type_id",
      "name": "current_translation",
      "macro": "has_one",
      "resource_name": null,
      "through": null
    },
    {
      "className": "FinanceAccount",
      "collectionName": "finance_accounts",
      "foreignKey": "finance_account_type_id",
      "name": "finance_accounts",
      "macro": "has_many",
      "resource_name": "FinanceAccount",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "FinanceAccountType::Translation",
      "collectionName": null,
      "foreignKey": "finance_account_type_id",
      "name": "translations",
      "macro": "has_many",
      "resource_name": null,
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "FinanceAccount",
      "collectionName": "finance_accounts",
      "foreignKey": "finance_account_type_id",
      "name": "finance_accounts",
      "macro": "has_many",
      "resource_name": "FinanceAccount",
      "through": null
    }
  ],
  "paramKey": "finance_account_type",
  "primaryKey": "id"
}

/** Frontend model for FinanceAccountType. */
export default class FinanceAccountType extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./finance-account.js").default>} */
  financeAccounts() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["finance_account_type_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "finance_accounts",
        model: this,
        modelClass: FinanceAccount
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./finance-account.js").default>>} */
  loadFinanceAccounts() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["finance_account_type_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "finance_accounts",
        model: this,
        modelClass: FinanceAccount
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./finance-account.js").default>>} */
  ensureFinanceAccountsLoaded() {
    return this.ensureAssociationLoaded("finance_accounts")
  }
}
